// Function index:
//   hexDistance(q1, r1, q2, r2)         — axial distance between two hexes.
//   isAdjacent(q1, r1, q2, r2)          — true when the second hex is one
//                                          of the six pointy-top neighbours.
//   STREAM_TRIGGER_DIST / STREAM_RADIUS — fog-streaming thresholds.
//   createStreamTrigger(start, onNeed)  — returns a `check(q, r)` that fires
//                                          onNeed when the player has walked
//                                          STREAM_TRIGGER_DIST from the last
//                                          streamed centre, then resets the
//                                          centre.
//
// Phase B3 streaming uses (player position, last streamed centre) plus a
// trigger distance well under BOOTSTRAP_HEX_RADIUS (30) so the loaded
// buffer always covers the player's neighbourhood.

import { NEIGHBOURS } from './palette.js';

export const STREAM_TRIGGER_DIST = 15;
export const STREAM_RADIUS = 30;

export function hexDistance(q1: number, r1: number, q2: number, r2: number): number {
  const dq = q1 - q2;
  const dr = r1 - r2;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr));
}

export function isAdjacent(q1: number, r1: number, q2: number, r2: number): boolean {
  return NEIGHBOURS.some(([dq, dr]) => q1 - q2 === dq && r1 - r2 === dr);
}

export type StreamCheck = (q: number, r: number) => void;

export function createStreamTrigger(
  start: { q: number; r: number },
  onNeed: () => ((q: number, r: number, radius: number) => void) | null,
): StreamCheck {
  let lastCenter = { q: start.q, r: start.r };
  return (q, r) => {
    const handler = onNeed();
    if (!handler) return;
    if (hexDistance(q, r, lastCenter.q, lastCenter.r) < STREAM_TRIGGER_DIST) return;
    lastCenter = { q, r };
    handler(q, r, STREAM_RADIUS);
  };
}
